import React from 'react';
import type { SnapCategory } from '@/types/database.types'; // Adjust path if needed
import { getCategoryColor } from '@/utils/snap'; // Adjust path if needed

// Define the props interface (assuming it's the same as before)
interface SnapCardPreviewProps {
  idForCanvas: string; // ID for html2canvas to target
  title: string;
  description: string;
  categories: SnapCategory[];
  options: { title: string; description: string; imageUrl: string; vote_count?: number }[];
  totalVotes?: number;
  creatorName?: string;
  timeRemaining?: string | number;
  viewCount?: number;
  commentCount?: number;
  likeCount?: number;
  isNew?: boolean;
  isFireSnap?: boolean;
}

const SnapCardPreview: React.FC<SnapCardPreviewProps> = ({
  idForCanvas,
  title,
  description,
  categories,
  options,
  totalVotes = 0,
  creatorName = 'Anonymous',
  timeRemaining,
  viewCount = 0,
  commentCount = 0,
  likeCount = 0,
  isNew = false,
  isFireSnap = false,
}) => {
  // Format time remaining text (same as before)
  let timeRemainingText = '';
  if (typeof timeRemaining === 'string') {
    timeRemainingText = timeRemaining;
  } else if (typeof timeRemaining === 'number') {
    const days = Math.ceil(timeRemaining / (1000 * 60 * 60 * 24));
    timeRemainingText = `in ${days} ${days === 1 ? 'day' : 'days'}`;
  }

  // Helper function to get background and text colors for categories
  const getCategoryStyles = (category: SnapCategory) => {
    const colors = getCategoryColor(category); // Assume this returns { bg: string, text: string, icon: string }

    // --- Restore the full color maps ---
    const bgColorMap: Record<string, string> = {
      'bg-blue-100': '#dbeafe',
      'bg-green-100': '#dcfce7',
      'bg-emerald-100': '#d1fae5',
      'bg-pink-100': '#fce7f3',
      'bg-purple-100': '#f3e8ff',
      'bg-indigo-100': '#e0e7ff',
      'bg-red-100': '#fee2e2',
      'bg-amber-100': '#fef3c7',
      'bg-orange-100': '#ffedd5',
      'bg-cyan-100': '#cffafe',
      'bg-gray-100': '#f3f4f6',
      'default': '#f3f4f6' // Default bg color
    };

    const textColorMap: Record<string, string> = {
      'text-blue-800': '#1e40af',
      'text-green-800': '#166534',
      'text-emerald-800': '#065f46',
      'text-pink-800': '#9d174d',
      'text-purple-800': '#5b21b6',
      'text-indigo-800': '#3730a3',
      'text-red-800': '#991b1b',
      'text-amber-800': '#92400e',
      'text-orange-800': '#9a3412',
      'text-cyan-800': '#155e75',
      'text-gray-800': '#1f2937',
      'default': '#1f2937' // Default text color
    };
    // --- End restoring color maps ---

    // Use retrieved class names or 'default' if not found/invalid
    const bgClass = colors?.bg && colors.bg in bgColorMap ? colors.bg : 'default';
    const textClass = colors?.text && colors.text in textColorMap ? colors.text : 'default';

    const bgColor = bgColorMap[bgClass];
    const textColor = textColorMap[textClass];

    return { bgColor, textColor, icon: colors?.icon || '❓' }; // Provide default icon if needed
  };


  // --- Calculate NEW padding values based on cumulative adjustments ---
  const originalTopPaddingTag = 1; // px
  const originalBottomPaddingTag = 1; // px (inferred)

  const originalTopPaddingOther = 0; // px
  const originalBottomPaddingOther = 2; // px

  // Total adjustments from original:
  const totalTopDelta = 15;  // 5px + 10px
  const totalBottomDelta = 10; // 5px + 5px

  const newTopPaddingTag = originalTopPaddingTag - totalTopDelta; // e.g., 1 - 15 = -14
  const newBottomPaddingTag = originalBottomPaddingTag + totalBottomDelta; // e.g., 1 + 10 = 11

  const newTopPaddingOther = originalTopPaddingOther - totalTopDelta; // e.g., 0 - 15 = -15
  const newBottomPaddingOther = originalBottomPaddingOther + totalBottomDelta; // e.g., 2 + 10 = 12
  // --- End NEW padding calculation ---


  return (
    <div
      id={idForCanvas}
      style={{
        width: '1000px',
        background: 'white',
        border: '2px solid #fb923c',
        borderRadius: '16px',
        padding: '30px',
        boxSizing: 'border-box',
        fontFamily: 'Arial, sans-serif',
        position: 'relative'
      }}
    >
      {/* Tags Section */}
      {(isNew || isFireSnap) && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          {isNew && (
            <div style={{ backgroundColor: '#dcfce7', color: '#166534', padding: '4px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '24px' }}>
              <span style={{ marginRight: '4px', paddingRight: '2px', /* Applied new change: */ paddingTop: `${newTopPaddingTag}px`, paddingBottom: `${newBottomPaddingTag}px` }}>✨</span>
              <span style={{ paddingRight: '2px', /* Applied new change: */ paddingTop: `${newTopPaddingTag}px`, paddingBottom: `${newBottomPaddingTag}px` }}>New</span>
            </div>
          )}
          {isFireSnap && (
            <div style={{ backgroundColor: '#ffedd5', color: '#9a3412', padding: '4px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '24px' }}>
              <span style={{ marginRight: '4px', paddingRight: '2px', /* Applied new change: */ paddingTop: `${newTopPaddingTag}px`, paddingBottom: `${newBottomPaddingTag}px` }}>🔥</span>
              <span style={{ paddingRight: '2px', /* Applied new change: */ paddingTop: `${newTopPaddingTag}px`, paddingBottom: `${newBottomPaddingTag}px` }}>FIRESNAP</span>
            </div>
          )}
        </div>
      )}

      {/* Title */}
      <div style={{ fontSize: '24px', fontWeight: 700, color: '#1f2937', marginBottom: '12px', lineHeight: '1.3' }}>
        {title}
      </div>

      {/* Description */}
      {description && (
        <div style={{ fontSize: '16px', color: '#4b5563', marginBottom: '16px', lineHeight: '1.5' }}>
          {description}
        </div>
      )}

      {/* Categories Section */}
      {categories && categories.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
          {categories.slice(0, 3).map(category => {
            // *** Use the retrieved colors ***
            const { bgColor, textColor, icon } = getCategoryStyles(category);
            return (
              <div
                key={category}
                // *** Apply the dynamic background and text colors ***
                style={{ background: bgColor, color: textColor, padding: '3px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', textTransform: 'capitalize', height: '24px' }}
              >
                <span style={{ marginRight: '4px', paddingRight: '2px', /* Applied new change: */ paddingTop: `${newTopPaddingOther}px`, paddingBottom: `${newBottomPaddingOther}px` }}>
                  {icon}
                </span>
                <span style={{ paddingRight: '2px', /* Applied new change: */ paddingTop: `${newTopPaddingOther}px`, paddingBottom: `${newBottomPaddingOther}px` }}>
                  {category}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Creator Info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '4px', paddingRight: '2px', /* Applied new change: */ paddingTop: `${newTopPaddingOther}px`, paddingBottom: `${newBottomPaddingOther}px` }}>👤</span>
          <span style={{ paddingRight: '2px', /* Applied new change: */ paddingTop: `${newTopPaddingOther}px`, paddingBottom: `${newBottomPaddingOther}px` }}>{creatorName}</span>
        </div>
        {timeRemainingText && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '4px', paddingRight: '2px', /* Applied new change: */ paddingTop: `${newTopPaddingOther}px`, paddingBottom: `${newBottomPaddingOther}px` }}>⏰</span>
            <span style={{ paddingRight: '2px', /* Applied new change: */ paddingTop: `${newTopPaddingOther}px`, paddingBottom: `${newBottomPaddingOther}px` }}>{timeRemainingText}</span>
          </div>
        )}
      </div>

      {/* Options/Poll Section */}
      <div style={{ marginBottom: '20px' }}>
        {options && options.slice(0, 3).map((option, index) => {
          const percentage = totalVotes > 0 ? ((option.vote_count || 0) / totalVotes * 100) : 0;
          const displayPercentage = percentage.toFixed(0);
          return (
            <div style={{ marginBottom: '16px' }} key={index}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '8px', color: '#374151' }}>
                <span>{option.title}</span>
                <strong>{displayPercentage}%</strong>
              </div>
              <div style={{ width: '100%', height: '6px', borderRadius: '4px', backgroundColor: '#e5e7eb', position: 'relative', overflow: 'hidden' }}>
                <div style={{ height: '100%', backgroundColor: '#3b82f6', borderRadius: '4px', width: `${percentage}%` }}></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Stats */}
      <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#6b7280', marginTop: '12px', borderTop: '1px solid rgba(229, 231, 235, 0.5)', paddingTop: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '4px', paddingRight: '2px', /* Applied new change: */ paddingTop: `${newTopPaddingOther}px`, paddingBottom: `${newBottomPaddingOther}px` }}>👁️</span>
          <span style={{ paddingRight: '2px', /* Applied new change: */ paddingTop: `${newTopPaddingOther}px`, paddingBottom: `${newBottomPaddingOther}px` }}>{viewCount}</span>
        </div>
        {/* Repeat for other stats */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
           <span style={{ marginRight: '4px', paddingRight: '2px', /* Applied new change: */ paddingTop: `${newTopPaddingOther}px`, paddingBottom: `${newBottomPaddingOther}px` }}>💬</span>
           <span style={{ paddingRight: '2px', /* Applied new change: */ paddingTop: `${newTopPaddingOther}px`, paddingBottom: `${newBottomPaddingOther}px` }}>{commentCount}</span>
        </div>
         <div style={{ display: 'flex', alignItems: 'center' }}>
           <span style={{ marginRight: '4px', paddingRight: '2px', /* Applied new change: */ paddingTop: `${newTopPaddingOther}px`, paddingBottom: `${newBottomPaddingOther}px` }}>❤️</span>
           <span style={{ paddingRight: '2px', /* Applied new change: */ paddingTop: `${newTopPaddingOther}px`, paddingBottom: `${newBottomPaddingOther}px` }}>{likeCount}</span>
        </div>
      </div>

      {/* Branding */}
      <div style={{ position: 'absolute', bottom: '16px', right: '16px', fontSize: '12px', color: '#9ca3af' }}>
        snapvote.ai
      </div>
    </div>
  );
};

export default SnapCardPreview;